'use client';

/**
 * Notifications Panel - Shows upcoming audits, renewals, and important alerts
 */

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import type { Entity, Assessment } from '@/lib/command-center/types';

interface Notification {
  id: string;
  type: 'audit' | 'renewal' | 'expiry' | 'assessment' | 'compliance';
  title: string;
  description: string;
  dueDate?: string;
  severity: 'info' | 'warning' | 'urgent';
  entityId?: string;
  entityName?: string;
  assessmentId?: string;
  link?: string;
}

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  entities: Entity[];
  assessments: Assessment[];
  onNotificationCount?: (count: number) => void;
}

export default function NotificationsPanel({
  isOpen,
  onClose,
  entities,
  assessments,
  onNotificationCount,
}: NotificationsPanelProps) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    // Load dismissed notifications from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('cc-dismissed-notifications');
      if (saved) {
        try {
          return new Set(JSON.parse(saved));
        } catch {
          return new Set();
        }
      }
    }
    return new Set();
  });

  // Generate notifications from entities and assessments
  const notifications = useMemo(() => {
    const items: Notification[] = [];
    const now = new Date();
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    const twoWeeks = 14 * 24 * 60 * 60 * 1000;
    const oneMonth = 30 * 24 * 60 * 60 * 1000;

    // Check entity metadata for renewal/audit dates
    entities.forEach(entity => {
      const metadata = entity.metadata || {};

      // Check for audit date
      if (metadata.next_audit_date) {
        const auditDate = new Date(metadata.next_audit_date as string);
        const diff = auditDate.getTime() - now.getTime();

        if (diff > 0 && diff < oneMonth) {
          items.push({
            id: `audit-${entity.id}`,
            type: 'audit',
            title: 'Upcoming Audit',
            description: `${entity.name} has an audit scheduled`,
            dueDate: metadata.next_audit_date as string,
            severity: diff < oneWeek ? 'urgent' : diff < twoWeeks ? 'warning' : 'info',
            entityId: entity.id,
            entityName: entity.name,
            link: `/command-center/entity?id=${entity.id}`,
          });
        }
      }

      // Check for license/renewal dates
      if (metadata.renewal_date) {
        const renewalDate = new Date(metadata.renewal_date as string);
        const diff = renewalDate.getTime() - now.getTime();

        if (diff > 0 && diff < oneMonth) {
          items.push({
            id: `renewal-${entity.id}`,
            type: 'renewal',
            title: 'License Renewal Due',
            description: `${entity.name} license renewal is approaching`,
            dueDate: metadata.renewal_date as string,
            severity: diff < oneWeek ? 'urgent' : diff < twoWeeks ? 'warning' : 'info',
            entityId: entity.id,
            entityName: entity.name,
            link: `/command-center/entity?id=${entity.id}`,
          });
        }
      }

      // Check for contract expiry
      if (metadata.contract_end_date) {
        const expiryDate = new Date(metadata.contract_end_date as string);
        const diff = expiryDate.getTime() - now.getTime();

        if (diff > 0 && diff < oneMonth) {
          items.push({
            id: `expiry-${entity.id}`,
            type: 'expiry',
            title: 'Contract Expiring',
            description: `${entity.name} contract is expiring soon`,
            dueDate: metadata.contract_end_date as string,
            severity: diff < oneWeek ? 'urgent' : diff < twoWeeks ? 'warning' : 'info',
            entityId: entity.id,
            entityName: entity.name,
            link: `/command-center/entity?id=${entity.id}`,
          });
        }
      }

      // Check for support expiry
      if (metadata.support_end_date) {
        const supportDate = new Date(metadata.support_end_date as string);
        const diff = supportDate.getTime() - now.getTime();

        if (diff > 0 && diff < oneMonth) {
          items.push({
            id: `support-${entity.id}`,
            type: 'expiry',
            title: 'Support Ending',
            description: `${entity.name} support is ending soon`,
            dueDate: metadata.support_end_date as string,
            severity: diff < oneWeek ? 'urgent' : diff < twoWeeks ? 'warning' : 'info',
            entityId: entity.id,
            entityName: entity.name,
            link: `/command-center/entity?id=${entity.id}`,
          });
        }
      }

      // Deprecated entities need attention
      if (entity.status === 'deprecated') {
        items.push({
          id: `deprecated-${entity.id}`,
          type: 'compliance',
          title: 'Deprecated Entity',
          description: `${entity.name} is deprecated and may need migration`,
          severity: 'warning',
          entityId: entity.id,
          entityName: entity.name,
          link: `/command-center/entity?id=${entity.id}`,
        });
      }
    });

    // Check assessments
    assessments.forEach(assessment => {
      // Assessments with expiration dates
      if (assessment.expires_at) {
        const expiryDate = new Date(assessment.expires_at);
        const diff = expiryDate.getTime() - now.getTime();

        if (diff > 0 && diff < oneMonth) {
          items.push({
            id: `assessment-expiry-${assessment.id}`,
            type: 'assessment',
            title: 'Assessment Expiring',
            description: `${assessment.name} assessment will expire soon`,
            dueDate: assessment.expires_at,
            severity: diff < oneWeek ? 'urgent' : diff < twoWeeks ? 'warning' : 'info',
            assessmentId: assessment.id,
            link: `/command-center/assessments`,
          });
        }
      }

      // In-progress assessments
      if (assessment.status === 'in_progress') {
        items.push({
          id: `assessment-progress-${assessment.id}`,
          type: 'assessment',
          title: 'Assessment In Progress',
          description: `${assessment.name} assessment needs completion`,
          severity: 'info',
          assessmentId: assessment.id,
          link: `/command-center/assessments`,
        });
      }

      // Low score alerts (below 50%)
      if (assessment.score_overall !== undefined && assessment.score_overall < 50 && assessment.status === 'completed') {
        items.push({
          id: `assessment-score-${assessment.id}`,
          type: 'compliance',
          title: 'Low Assessment Score',
          description: `${assessment.name} scored ${assessment.score_overall}% - needs attention`,
          severity: 'warning',
          assessmentId: assessment.id,
          link: `/command-center/assessments`,
        });
      }
    });

    // Filter out dismissed notifications
    return items
      .filter(n => !dismissedIds.has(n.id))
      .sort((a, b) => {
        // Sort by severity first, then by date
        const severityOrder = { urgent: 0, warning: 1, info: 2 };
        if (severityOrder[a.severity] !== severityOrder[b.severity]) {
          return severityOrder[a.severity] - severityOrder[b.severity];
        }
        if (a.dueDate && b.dueDate) {
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        }
        return 0;
      });
  }, [entities, assessments, dismissedIds]);

  // Report notification count to parent
  useEffect(() => {
    onNotificationCount?.(notifications.length);
  }, [notifications.length, onNotificationCount]);

  const dismissNotification = (id: string) => {
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      // Save to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('cc-dismissed-notifications', JSON.stringify([...next]));
      }
      return next;
    });
  };

  const clearAllDismissed = () => {
    setDismissedIds(new Set());
    if (typeof window !== 'undefined') {
      localStorage.removeItem('cc-dismissed-notifications');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = Math.ceil(diff / (24 * 60 * 60 * 1000));

    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days < 7) return `In ${days} days`;
    if (days < 14) return `In ${Math.ceil(days / 7)} week`;
    return date.toLocaleDateString();
  };

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'audit':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'renewal':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'expiry':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'assessment':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      case 'compliance':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
    }
  };

  const getSeverityColors = (severity: Notification['severity']) => {
    switch (severity) {
      case 'urgent':
        return 'bg-red-900/50 border-red-500/30 text-red-400';
      case 'warning':
        return 'bg-yellow-900/50 border-yellow-500/30 text-yellow-400';
      case 'info':
        return 'bg-blue-900/50 border-blue-500/30 text-blue-400';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose}>
      <div
        className="absolute top-16 right-4 w-96 max-h-[80vh] bg-background border border-border rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-muted bg-surface/30">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
            {notifications.length > 0 && (
              <span className="bg-accent text-accent-foreground text-xs font-bold px-2 py-0.5 rounded-full">
                {notifications.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-foreground-tertiary hover:text-foreground transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Notification List */}
        <div className="max-h-[60vh] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="w-12 h-12 text-foreground-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="text-foreground-tertiary">No notifications</p>
              <p className="text-foreground-muted text-sm mt-1">You&apos;re all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-border-muted">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="p-4 hover:bg-cream/5 transition-colors"
                >
                  <div className="flex gap-3">
                    <div className={`p-2 rounded-lg ${getSeverityColors(notification.severity)}`}>
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="text-foreground font-medium text-sm">
                            {notification.title}
                          </h3>
                          <p className="text-foreground-tertiary text-sm mt-0.5">
                            {notification.description}
                          </p>
                        </div>
                        <button
                          onClick={() => dismissNotification(notification.id)}
                          className="text-foreground-muted hover:text-foreground transition-colors p-1"
                          title="Dismiss"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        {notification.dueDate && (
                          <span className={`text-xs ${
                            notification.severity === 'urgent' ? 'text-red-400' :
                            notification.severity === 'warning' ? 'text-yellow-400' :
                            'text-foreground-tertiary'
                          }`}>
                            {formatDate(notification.dueDate)}
                          </span>
                        )}
                        {notification.link && (
                          <Link
                            href={notification.link}
                            onClick={onClose}
                            className="text-xs text-accent hover:underline"
                          >
                            View details
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {dismissedIds.size > 0 && (
          <div className="px-4 py-3 border-t border-border-muted bg-surface/30">
            <button
              onClick={clearAllDismissed}
              className="text-sm text-foreground-tertiary hover:text-foreground transition-colors"
            >
              Show {dismissedIds.size} dismissed notification{dismissedIds.size !== 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
